import React, { useState } from 'react';

import styled from '@emotion/styled';
import { faFile, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { COLORS } from '@root/colors';
import { Alert, Button, FormGroup, Modal } from '@shared/components/bootstrap';
import { FileFormControl } from '@shared/components/fields/file_form_control';
import { Spacer } from '@shared/components/helpers';

import { SelectFormGroup } from '@admin/components/fields';
import { Panel } from '@admin/components/helpers/panel';
import { logisticsLoadBolURL } from '@admin/config/routes';
import { client } from '@admin/libraries/apollo';
import { AttachmentFragment, LogisticsLoadQuery, Status, useLoadDocumentsUpdateMutation } from '@admin/schema';

enum DocumentType {
  CartonLabels = 'Carton Labels',
  PalletLabels = 'Pallet Labels',
  PalletizationGuide = 'Palletization Guide',
  SmallParcelLabels = 'Small Parcel Labels',
  CustomBillOfLading = 'Custom Bill of Lading',
  Other = 'Other',
}

const UploadDocumentModal: React.FC<{
  loadID: string;
  billOfLadingExistingFileSignedIDs: string[];
  cartonLabelExistingFileSignedIDs: string[];
  otherDocumentExistingFileSignedIDs: string[];
  palletizationGuideExistingFileSignedIDs: string[];
  palletLabelExistingFileSignedIDs: string[];
  smallParcelLabelExistingFileSignedIDs: string[];
  onClose: (refetchLoad?: boolean) => void;
}> = ({
  loadID,
  billOfLadingExistingFileSignedIDs,
  cartonLabelExistingFileSignedIDs,
  otherDocumentExistingFileSignedIDs,
  palletizationGuideExistingFileSignedIDs,
  palletLabelExistingFileSignedIDs,
  smallParcelLabelExistingFileSignedIDs,
  onClose,
}) => {
  const [file, setFile] = useState<File>();
  const [fileSignedID, setFileSignedID] = useState<string>();
  const [documentType, setDocumentType] = useState<DocumentType>();
  const [error, setError] = useState<string | null>();

  const [execute, { loading }] = useLoadDocumentsUpdateMutation({ client });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fileSignedID || !documentType) return;

    setError(null);
    const { data, errors } = await execute({
      variables: {
        input: {
          loadID,
          cartonLabelFileSignedIDs:
            documentType === DocumentType.CartonLabels
              ? [...cartonLabelExistingFileSignedIDs, fileSignedID]
              : cartonLabelExistingFileSignedIDs,
          palletLabelFileSignedIDs:
            documentType === DocumentType.PalletLabels
              ? [...palletLabelExistingFileSignedIDs, fileSignedID]
              : palletLabelExistingFileSignedIDs,
          palletizationGuideFileSignedIDs:
            documentType === DocumentType.PalletizationGuide
              ? [...palletizationGuideExistingFileSignedIDs, fileSignedID]
              : palletizationGuideExistingFileSignedIDs,
          smallParcelLabelFileSignedIDs:
            documentType === DocumentType.SmallParcelLabels
              ? [...smallParcelLabelExistingFileSignedIDs, fileSignedID]
              : smallParcelLabelExistingFileSignedIDs,
          billOfLadingFileSignedIDs:
            documentType === DocumentType.CustomBillOfLading
              ? [...billOfLadingExistingFileSignedIDs, fileSignedID]
              : billOfLadingExistingFileSignedIDs,
          otherDocumentFileSignedIDs:
            documentType === DocumentType.Other
              ? [...otherDocumentExistingFileSignedIDs, fileSignedID]
              : otherDocumentExistingFileSignedIDs,
        },
      },
    });

    if (data?.loadDocumentsUpdate.status === Status.Ok) {
      setFile(undefined);
      setFileSignedID(undefined);
      setDocumentType(undefined);
      onClose(true);
    } else {
      setError(data?.loadDocumentsUpdate.error || errors?.[0]?.message || 'Unknown error');
    }
  };

  return (
    <Modal onClose={onClose}>
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>Upload Document</Modal.Title>
          <Modal.Close close={onClose} />
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert style="danger">
              <strong>Error: </strong>
              {error}
            </Alert>
          )}
          <Alert style="warning">Uploaded documents are visible to the customer in their account portal.</Alert>
          <Spacer height="16px" />
          <SelectFormGroup
            id="documentType"
            label="Document Type"
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value as DocumentType)}
          >
            <option value="">Select Document Type</option>
            {Object.values(DocumentType).map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </SelectFormGroup>
          <FormGroup>
            <FileFormControl
              id="newLoadDocumentSignedID"
              accept="application/pdf"
              label={file?.name}
              onUpload={(newFile, newSignedID) => {
                setFile(newFile);
                setFileSignedID(newSignedID);
              }}
            />
          </FormGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => onClose()} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={onSubmit} loading={loading} kind="primary">
            Upload
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};

const DeleteDocumentModal: React.FC<{
  loadID: string;
  fileSignedIDToDelete: string;
  billOfLadingExistingFileSignedIDs: string[];
  cartonLabelExistingFileSignedIDs: string[];
  otherDocumentExistingFileSignedIDs: string[];
  palletizationGuideExistingFileSignedIDs: string[];
  palletLabelExistingFileSignedIDs: string[];
  smallParcelLabelExistingFileSignedIDs: string[];
  onClose: (refetchLoad?: boolean) => void;
}> = ({
  loadID,
  fileSignedIDToDelete,
  billOfLadingExistingFileSignedIDs,
  cartonLabelExistingFileSignedIDs,
  otherDocumentExistingFileSignedIDs,
  palletizationGuideExistingFileSignedIDs,
  palletLabelExistingFileSignedIDs,
  smallParcelLabelExistingFileSignedIDs,
  onClose,
}) => {
  const [error, setError] = useState<string | null>();
  const [execute, { loading }] = useLoadDocumentsUpdateMutation({ client });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const removeID = (ids: string[]) => ids.filter((id) => id !== fileSignedIDToDelete);

    const { data, errors } = await execute({
      variables: {
        input: {
          loadID,
          cartonLabelFileSignedIDs: removeID(cartonLabelExistingFileSignedIDs),
          palletLabelFileSignedIDs: removeID(palletLabelExistingFileSignedIDs),
          palletizationGuideFileSignedIDs: removeID(palletizationGuideExistingFileSignedIDs),
          smallParcelLabelFileSignedIDs: removeID(smallParcelLabelExistingFileSignedIDs),
          billOfLadingFileSignedIDs: removeID(billOfLadingExistingFileSignedIDs),
          otherDocumentFileSignedIDs: removeID(otherDocumentExistingFileSignedIDs),
        },
      },
    });

    if (data?.loadDocumentsUpdate.status === Status.Ok) {
      onClose(true);
    } else {
      setError(data?.loadDocumentsUpdate.error || errors?.[0]?.message || 'Unknown error');
    }
  };

  return (
    <Modal onClose={onClose}>
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>Delete Document</Modal.Title>
          <Modal.Close close={onClose} />
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this document?</p>
          {error && (
            <Alert style="danger">
              <strong>Error: </strong>
              {error}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => onClose()} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={onSubmit} loading={loading} kind="danger">
            Delete
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};

const StyledIcon = styled(FontAwesomeIcon)`
  margin: 0 5px 0 0;
  color: ${COLORS.grayDarkest};
`;

const FileBox = styled.div`
  padding: 16px;
  border: 1px solid ${COLORS.grayLighter};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  width: 200px;
`;

export const LogisticsLoadDocumentsPanel: React.FC<{
  load: LogisticsLoadQuery['load'];
  refetching: boolean;
  refetch: () => void;
}> = ({ load, refetching, refetch }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [isUploadModalOpen, setUploadModalOpen] = useState(false);
  const [confirmDeleteSignedID, setConfirmDeleteSignedID] = useState<string>();

  const renderFile = (file: AttachmentFragment) => (
    <>
      <a href={file.url} target="_blank" rel="noopener noreferrer">
        <StyledIcon icon={faFile} size="3x" />
      </a>
      <div style={{ fontSize: '0.9rem', marginTop: '0.5rem', wordBreak: 'break-word' }}>{file.filename}</div>
      <Button size="sm" kind="danger" onClick={() => setConfirmDeleteSignedID(file.signedID)} disabled={refetching}>
        <FontAwesomeIcon icon={faTrash} /> Delete
      </Button>
    </>
  );

  const renderFileSection = (files: AttachmentFragment[], type: DocumentType) => {
    if (!files.length) return null;
    return (
      <>
        <h6>{type}</h6>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '1rem' }}>
          {files.map((file) => (
            <FileBox key={file.id}>{renderFile(file)}</FileBox>
          ))}
        </div>
      </>
    );
  };

  return (
    <>
      <Panel>
        <Panel.Header>
          <Panel.Collapse collapsed={collapsed} onChange={setCollapsed} />
          <Panel.Title>Load Documents</Panel.Title>
        </Panel.Header>
        <Panel.Body>
          {!collapsed && (
            <>
              <h6>Clutter Bill of Lading</h6>
              <FileBox>
                <a href={logisticsLoadBolURL({ id: load.id })} target="_blank" rel="noopener noreferrer">
                  <StyledIcon icon={faFile} size="3x" />
                </a>
                <div style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>Clutter Bill of Lading</div>
              </FileBox>

              {renderFileSection(load.customerUploadedBillsOfLading, DocumentType.CustomBillOfLading)}
              {renderFileSection(load.customerUploadedCartonLabels, DocumentType.CartonLabels)}
              {renderFileSection(load.customerUploadedPalletLabels, DocumentType.PalletLabels)}
              {renderFileSection(load.customerUploadedPalletizationGuides, DocumentType.PalletizationGuide)}
              {renderFileSection(load.customerUploadedSmallParcelLabels, DocumentType.SmallParcelLabels)}
              {renderFileSection(load.customerUploadedAdditionalFiles, DocumentType.Other)}

              <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                <Button kind="success" onClick={() => setUploadModalOpen(true)} disabled={refetching}>
                  <FontAwesomeIcon icon={faPlus} /> Upload Document
                </Button>
              </div>
            </>
          )}
        </Panel.Body>
      </Panel>

      {isUploadModalOpen && (
        <UploadDocumentModal
          loadID={load.id}
          billOfLadingExistingFileSignedIDs={load.customerUploadedBillsOfLading.map((f) => f.signedID)}
          cartonLabelExistingFileSignedIDs={load.customerUploadedCartonLabels.map((f) => f.signedID)}
          otherDocumentExistingFileSignedIDs={load.customerUploadedAdditionalFiles.map((f) => f.signedID)}
          palletizationGuideExistingFileSignedIDs={load.customerUploadedPalletizationGuides.map((f) => f.signedID)}
          palletLabelExistingFileSignedIDs={load.customerUploadedPalletLabels.map((f) => f.signedID)}
          smallParcelLabelExistingFileSignedIDs={load.customerUploadedSmallParcelLabels.map((f) => f.signedID)}
          onClose={(refetchLoad) => {
            if (refetchLoad) refetch();
            setUploadModalOpen(false);
          }}
        />
      )}

      {confirmDeleteSignedID && (
        <DeleteDocumentModal
          loadID={load.id}
          fileSignedIDToDelete={confirmDeleteSignedID}
          billOfLadingExistingFileSignedIDs={load.customerUploadedBillsOfLading.map((f) => f.signedID)}
          cartonLabelExistingFileSignedIDs={load.customerUploadedCartonLabels.map((f) => f.signedID)}
          otherDocumentExistingFileSignedIDs={load.customerUploadedAdditionalFiles.map((f) => f.signedID)}
          palletizationGuideExistingFileSignedIDs={load.customerUploadedPalletizationGuides.map((f) => f.signedID)}
          palletLabelExistingFileSignedIDs={load.customerUploadedPalletLabels.map((f) => f.signedID)}
          smallParcelLabelExistingFileSignedIDs={load.customerUploadedSmallParcelLabels.map((f) => f.signedID)}
          onClose={(refetchLoad) => {
            if (refetchLoad) refetch();
            setConfirmDeleteSignedID(undefined);
          }}
        />
      )}
    </>
  );
};
